import React, { useState, useEffect, useCallback } from 'react';
import { THEME } from '../../constants/theme';
import API from '../../services/api';

// External Components
import SuppliersSection from './SuppliersSection';
import AddPurchaseForm from './AddPurchaseForm';
import BikeLifeCycleView from './BikeLifeCycleView'; 
import StoreBikeForm from './StoreBikeForm';
import VersionSection from './VersionSection';
import InventoryListSection from './InventoryListSection';

// ==========================================
// 📌 SUB-COMPONENT: REGISTRATION DESK
// ==========================================
const RegistrationDesk = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [bike, setBike] = useState(null);
    const [regNumber, setRegNumber] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSearch = async () => {
        if (!searchTerm) return;
        setLoading(true);
        try {
            const res = await API.get(`/v1/bikes/search-by-identity?term=${searchTerm}`);
            if (res.data.success) {
                setBike(res.data.data);
                setRegNumber(res.data.data.current_reg || '');
            } else {
                alert("Bike not found!");
                setBike(null);
            }
        } catch (err) { alert("Error searching bike"); }
        finally { setLoading(false); }
    };

    const handleAssign = async () => {
        if (!regNumber) return alert("Please enter a number");
        try {
            const res = await API.post('/v1/bikes/assign-number', {
                bike_master_id: bike.bike_master_id,
                reg_number: regNumber
            });
            if (res.data.success) {
                alert("Registration Successful!");
                setBike(null);
                setSearchTerm('');
            }
        } catch (err) { alert("Failed to assign number"); }
    };

    return (
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={styles.tableCard}>
                <div style={{ padding: '30px' }}>
                    <h3 style={{ marginTop: 0 }}>Registration Assignment</h3>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                        <input 
                            style={styles.searchInput} 
                            placeholder="Engine or Chassis #" 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button onClick={handleSearch} style={styles.searchBtn}>{loading ? '...' : 'Search'}</button>
                    </div>

                    {bike && (
                        <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                            <div style={{ marginBottom: '15px' }}>
                                <strong>{bike.model_name}</strong>
                                <div style={{ fontSize: '13px', color: '#64748b' }}>E: {bike.engine_number}</div>
                            </div>
                            <input 
                                style={{ ...styles.searchInput, fontSize: '22px', textAlign: 'center', fontWeight: 'bold', textTransform: 'uppercase' }} 
                                placeholder="ABC-1234" 
                                value={regNumber}
                                onChange={(e) => setRegNumber(e.target.value)}
                            />
                            <button onClick={handleAssign} style={styles.saveBtn}>Save Registration</button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ==========================================
// 📊 SUB-COMPONENT: INVENTORY SECTION
// ==========================================
const InventorySection = () => {
    const [stock, setStock] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');
    const [viewingBikeId, setViewingBikeId] = useState(null);

    const limit = 10;

    const fetchStock = useCallback(async () => {
        setLoading(true);
        try {
            const res = await API.get(`/v1/inventory/live-stock`, {
                params: { page: currentPage, limit: limit, search: search }
            });
            setStock(res.data.data || []);
            setTotalRecords(res.data.total || 0);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, [currentPage, search]);

    useEffect(() => {
        const delayDebounce = setTimeout(() => fetchStock(), 500);
        return () => clearTimeout(delayDebounce);
    }, [fetchStock]);

    const totalPages = Math.ceil(totalRecords / limit);

    return (
        <div>
            <div style={styles.statsRow}>
                <div style={styles.statCard}>
                    <span style={styles.statLabel}>Total Units</span>
                    <span style={styles.statValue}>{totalRecords}</span>
                </div>
                <div style={{ ...styles.statCard, flex: 2 }}>
                    <span style={styles.statLabel}>Quick Search</span>
                    <input 
                        placeholder="Search Engine, Chassis or Model..."
                        style={styles.searchInput}
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                    />
                </div>
            </div>

            <div style={styles.tableCard}>
                <table style={styles.table}>
                    <thead style={styles.thead}>
                        <tr>
                            <th style={styles.th}>Model & Color</th>
                            <th style={styles.th}>Chassis / Engine</th>
                            <th style={styles.th}>Cost</th>
                            <th style={styles.th}>Status</th>
                            <th style={styles.th}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" style={styles.loader}>Loading Inventory Data...</td></tr>
                        ) : stock.length > 0 ? (
                            stock.map((item) => (
                                <tr key={item.id} style={styles.tr}>
                                    <td style={styles.td}>
                                        <div style={{ fontWeight: 'bold' }}>{item.model_name}</div>
                                        <div style={{ fontSize: '11px', color: '#64748b' }}>{item.color}</div>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={{ fontSize: '12px' }}>C: {item.chassis_number}</div>
                                        <div style={{ fontSize: '12px' }}>E: {item.engine_number}</div>
                                    </td>
                                    <td style={{ ...styles.td, fontWeight: 'bold' }}>Rs. {Number(item.purchase_price).toLocaleString()}</td>
                                    <td style={styles.td}><span style={styles.statusBadge}>{item.stock_status}</span></td>
                                    <td style={styles.td}>
    <button 
    onClick={() => {
        // Step 1: ID uthayein (jo 36:1 ho sakti hai)
        const rawId = item.bike_master_id || item.id;
        
        // Step 2: Colon (:) ko khatam karke sirf number nikalein
        // Ye "36:1" ko "36" bana dega
        const cleanId = String(rawId).split(':')[0]; 

        console.log("Requesting clean ID:", cleanId); // Check karein console mein sirf "36" aana chahiye
        setViewingBikeId(cleanId);
    }} 
    style={styles.viewBtn}
>
    View Profile
</button>
                            </td>
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="5" style={{padding: '20px', textAlign: 'center'}}>No Records Found</td></tr>
                        )}
                    </tbody>
                </table>
                <div style={styles.paginationRow}>
                    <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={styles.pageBtn}>Prev</button>
                    <span style={{ fontSize: '13px' }}>Page {currentPage} of {totalPages}</span>
                    <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} style={styles.pageBtn}>Next</button>
                </div>
            </div>

            {/* 🛡️ BIKE LIFE CYCLE DIALOG (Isi Section ke andar connect kar diya) */}
            {viewingBikeId && (
                <BikeLifeCycleView 
                    bikeId={viewingBikeId} 
                    onClose={() => setViewingBikeId(null)} 
                />
            )}
        </div>
    );
};

// ==========================================
// 🏗️ MAIN COMPONENT: PURCHASE PANEL
// ==========================================

const VersionsSection = () => {
    const [data, setData] = useState([]);
    const [newVer, setNewVer] = useState({ model_name: '', version: '' });

    const fetchVersions = async () => {
        const res = await API.get('/v1/versions');
        setData(res.data.data);
    };

    const handleSave = async () => {
        await API.post('/v1/versions', newVer);
        setNewVer({ model_name: '', version: '' });
        fetchVersions();
    };

    const handleDelete = async (id) => {
        if(window.confirm('Are you sure?')) {
            await API.delete(`/v1/versions/${id}`);
            fetchVersions();
        }
    };

    useEffect(() => { fetchVersions(); }, []);

    return (
        <div style={styles.tableCard}>
            <style>{`
                .ver-row:hover { background: #f8fafc; }
                .ver-del:hover { background: #fecaca !important; }
                .ver-input:focus { border-color: #10b981 !important; box-shadow: 0 0 0 3px rgba(16,185,129,0.15); }
            `}</style>

            {/* Header */}
            <div style={styles.verHeader}>
                <div>
                    <h3 style={styles.verTitle}>⚙️ Makes & Versions</h3>
                   
                </div>
                <div style={styles.verCount}>{data.length} <span style={{ fontSize: '11px', fontWeight: 700 }}>Total</span></div>
            </div>

            {/* Add form */}
            <div style={styles.verForm}>
                <div style={{ flex: 1 }}>
                    <label style={styles.verLabel}>Make</label>
                    <input
                        className="ver-input"
                        placeholder="e.g. Honda CD-70"
                        value={newVer.model_name}
                        onChange={e => setNewVer({ ...newVer, model_name: e.target.value })}
                        style={styles.verInput}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <label style={styles.verLabel}>Version</label>
                    <input
                        className="ver-input"
                        placeholder="e.g. Deluxe / Standard"
                        value={newVer.version}
                        onChange={e => setNewVer({ ...newVer, version: e.target.value })}
                        style={styles.verInput}
                    />
                </div>
                <button onClick={handleSave} style={styles.verAddBtn}>+ Add Version</button>
            </div>

            {/* Table */}
            <table style={styles.table}>
                <thead style={styles.thead}>
                    <tr>
                        <th style={styles.th}>#</th>
                        <th style={styles.th}>Make</th>
                        <th style={styles.th}>Version</th>
                        <th style={styles.th}>Date Added</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr>
                            <td colSpan="5" style={styles.verEmpty}>
                                <div style={{ fontSize: '34px', marginBottom: '6px' }}>⚙️</div>
                                Abhi koi version add nahi hua.
                            </td>
                        </tr>
                    ) : data.map((item, idx) => (
                        <tr key={item.id} className="ver-row" style={styles.tr}>
                            <td style={{ ...styles.td, color: '#94a3b8', fontWeight: 'bold', width: '50px' }}>{idx + 1}</td>
                            <td style={{ ...styles.td, fontWeight: 'bold', color: '#1e293b' }}>{item.model_name}</td>
                            <td style={styles.td}><span style={styles.verPill}>{item.version}</span></td>
                            <td style={{ ...styles.td, color: '#64748b' }}>{new Date(item.created_at).toLocaleDateString()}</td>
                            <td style={{ ...styles.td, textAlign: 'right' }}>
                                <button className="ver-del" onClick={() => handleDelete(item.id)} style={styles.verDelBtn}>🗑 Delete</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const PurchasePanel = ({ initialSubTab }) => {
    const [activeSubTab, setActiveSubTab] = useState(initialSubTab || 'inventory');

    const menuItems = [
      
       // { id: 'registration', label: 'Reg. Desk', icon: '📌' },
        { id: 'add_purchase', label: 'Add Stock', icon: '➕' },
        { id: 'suppliers', label: 'Suppliers', icon: '🚛' },
        { id: 'versions', label: 'Bike Spec', icon: '⚙️' },
        { id: 'store_bike', label: 'Add Store Bike', icon: '🚲' },
          { id: 'inventory', label: 'Purchase List', icon: '📊' },
    ];

    return (
        <div style={styles.wrapper}>
            <div style={styles.container}>
                <div style={styles.topMenu}>
                    {menuItems.map((item) => (
                        <div 
                            key={item.id}
                            onClick={() => setActiveSubTab(item.id)}
                            style={{
                                ...styles.menuItem,
                                borderBottom: activeSubTab === item.id ? `3px solid ${THEME.secondary}` : '3px solid transparent',
                                color: activeSubTab === item.id ? THEME.secondary : '#64748b',
                                backgroundColor: activeSubTab === item.id ? '#f8fafc' : 'transparent',
                            }}
                        >
                            <span>{item.icon}</span>
                            <span>{item.label}</span>
                        </div>
                    ))}
                </div>
                <div style={styles.contentArea}>
                   {activeSubTab === 'inventory' && <InventoryListSection />}
                    {activeSubTab === 'registration' && <RegistrationDesk />}
                    {activeSubTab === 'add_purchase' && <AddPurchaseForm onComplete={() => setActiveSubTab('inventory')} />}
                  {activeSubTab === 'store_bike' && <StoreBikeForm onComplete={() => setActiveSubTab('inventory')} />}
                    {activeSubTab === 'suppliers' && <SuppliersSection />}
                  {activeSubTab === 'versions' && <VersionSection />}
                </div>
            </div>
        </div>
    );
};

const styles = {
    wrapper: { height: '100%', display: 'flex', flexDirection: 'column' },
    container: { display: 'flex', flexDirection: 'column', backgroundColor: '#fff', borderRadius: '16px', overflow: 'hidden', flex: 1, border: '1px solid #e2e8f0' },
    topMenu: { display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#fff' },
    menuItem: { display: 'flex', alignItems: 'center', padding: '18px 25px', cursor: 'pointer', gap: '10px', fontWeight: 'bold', fontSize: '14px', transition: '0.3s' },
    contentArea: { flex: 1, padding: '25px', backgroundColor: '#f8fafc', overflowY: 'auto' },
    statsRow: { display: 'flex', gap: '20px', marginBottom: '25px' },
    statCard: { background: '#fff', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', flex: 1 },
    statLabel: { fontSize: '11px', fontWeight: '900', color: '#64748b', textTransform: 'uppercase' },
    statValue: { fontSize: '24px', fontWeight: '900', color: '#1e293b', display: 'block' },
    searchInput: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px' },
    tableCard: { background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    thead: { background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    th: { padding: '15px', textAlign: 'left', fontSize: '11px', color: '#64748b', textTransform: 'uppercase' },
    tr: { borderBottom: '1px solid #f1f5f9', transition: '0.2s' },
    td: { padding: '15px', fontSize: '13px' },
    loader: { padding: '40px', textAlign: 'center', color: '#64748b' },
    statusBadge: { background: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: 'bold' },
    viewBtn: { padding: '8px 15px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' },
    searchBtn: { padding: '0 25px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' },
    saveBtn: { width: '100%', padding: '15px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', marginTop: '15px' },
    paginationRow: { padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' },
    pageBtn: { padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '12px' },

    // Versions section
    verHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', background: 'linear-gradient(135deg, #0f172a, #1e293b)', color: '#fff' },
    verTitle: { margin: 0, fontSize: '18px', fontWeight: 800 },
    verSub: { fontSize: '12px', color: '#94a3b8', marginTop: '4px' },
    verCount: { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', padding: '8px 16px', fontSize: '22px', fontWeight: 900, textAlign: 'center' },
    verForm: { display: 'flex', gap: '14px', alignItems: 'flex-end', padding: '20px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    verLabel: { display: 'block', fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
    verInput: { width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '14px', boxSizing: 'border-box', background: '#fff', transition: '0.2s' },
    verAddBtn: { padding: '11px 22px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap', boxShadow: '0 4px 10px rgba(16,185,129,0.3)' },
    verPill: { background: '#ecfdf5', color: '#059669', padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 'bold' },
    verDelBtn: { background: '#fee2e2', color: '#dc2626', border: 'none', padding: '7px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', transition: '0.2s' },
    verEmpty: { padding: '50px 20px', textAlign: 'center', color: '#94a3b8', fontWeight: 600 }
};

export default PurchasePanel;